/**
 * Step 6: the chapters, as the read found them. Rename one, join it with the
 * next, or split it in two. Splitting is drawn, not typed: the chapter as a
 * waveform with its two halves named, a scissors handle that snaps to where a
 * sentence starts (so a chapter never opens mid-word), the talk's own frame at
 * the cut, and the line said on either side of it.
 *
 * Nothing is saved until the host moves on: the rows go to the server with the
 * page design, as the chapters gate always took them.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import type { BoardState, BoardStateResponse } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { attachHls } from "../hls";
import { StepHead } from "./MakeFrame";
import { mmss, secondsAt } from "./time";

export interface ChapterRow {
  t: number;
  title: string;
  sub: string;
}

export interface TalkLine {
  ts: string;
  said: string;
}

/** The chapters card's rows, as the board holds them. */
export function chapterRowsOf(state: BoardState): ChapterRow[] {
  const card = state.page.find((c) => c.kind === "chapters");
  return card && card.kind === "chapters" ? card.rows.map((r) => ({ t: r.t, title: r.title, sub: r.sub })) : [];
}

/** Where chapter `i` ends: the next one's start, or the talk's end. */
export function chapterEnd(rows: readonly ChapterRow[], i: number, duration: number): number {
  return i < rows.length - 1 ? rows[i + 1].t : Math.max(duration, rows[i]?.t ?? 0);
}

/** The sentences that start inside chapter `i`, after its first second: the only places it can be cut. */
function cutsIn(rows: readonly ChapterRow[], i: number, duration: number, lines: readonly TalkLine[]): Array<{ t: number; said: string }> {
  const start = rows[i]?.t ?? 0;
  const end = chapterEnd(rows, i, duration);
  return lines
    .map((l) => ({ t: secondsAt(l.ts) ?? -1, said: l.said }))
    .filter((c) => c.t > start && c.t < end);
}

/** The line said just before second `t`: the end of the first half. */
function lineBefore(lines: readonly TalkLine[], t: number): string {
  let best = "";
  for (const l of lines) {
    const s = secondsAt(l.ts);
    if (s !== null && s < t) best = l.said;
  }
  return best;
}

/** A name for the chapter a cut starts: its first words, cut on a word, until the host types one. */
function nameFromLine(said: string): string {
  const clean = said.replace(/\s+/g, " ").trim().replace(/[.?!,;:]+$/, "");
  if (clean.length <= 48) return clean;
  const cut = clean.slice(0, 48);
  const space = cut.lastIndexOf(" ");
  return `${(space > 24 ? cut.slice(0, space) : cut).replace(/[,;:]$/, "")}…`;
}

/** Splits chapter `i` at second `t` with the new chapter named `title`. */
function splitRows(rows: readonly ChapterRow[], i: number, t: number, title: string): ChapterRow[] {
  const next = rows.slice();
  next.splice(i + 1, 0, { t, title: title.trim() || "New chapter", sub: "" });
  return next;
}

/** Chapter `i` takes in the one after it. */
function joinRows(rows: readonly ChapterRow[], i: number): ChapterRow[] {
  if (i >= rows.length - 1) return rows.slice();
  return rows.filter((_, k) => k !== i + 1);
}

const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];
export function chapterCount(rows: readonly ChapterRow[], duration: number): string {
  const n = rows.length;
  const word = WORDS[n] ?? String(n);
  const mins = Math.max(1, Math.round(duration / 60));
  return `${word} ${n === 1 ? "chapter" : "chapters"}${duration ? `, ${mins} minutes` : ""}`;
}

const BARS = 96;
/** A speaking waveform: steady bars that drop quiet where a sentence starts. */
function waveBars(start: number, end: number, cuts: readonly number[]): number[] {
  const span = Math.max(1, end - start);
  return Array.from({ length: BARS }, (_, b) => {
    const t = start + ((b + 0.5) / BARS) * span;
    const quiet = cuts.some((c) => Math.abs(t - c) < (span / BARS) * 1.4);
    return quiet ? 8 : Math.min(100, 26 + Math.round(Math.abs(Math.sin(b * 1.7) * 38 + Math.sin(b * 0.37 + 1) * 26)));
  });
}

export interface Media {
  url: string | null;
  token: string | null;
  poster: string | null;
  duration: number;
}

/** One `<video>` for the talk, playing a stretch or showing one frame. */
function useTalk(media: Media) {
  const ref = useRef<HTMLVideoElement>(null);
  const stopAt = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  /** The second the talk is at while it plays, for the player's time and its bar. */
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !media.url) return;
    const handle = attachHls(el, media.url, media.token);
    return () => {
      el.pause();
      handle.destroy();
    };
  }, [media.url, media.token]);
  const seek = useCallback((t: number) => {
    const el = ref.current;
    if (!el) return;
    try {
      el.currentTime = t;
    } catch {
      // Seeking before the recording has loaded does nothing; the poster stays.
    }
  }, []);
  const play = useCallback((from: number, to: number) => {
    const el = ref.current;
    if (!el) return;
    stopAt.current = to;
    try {
      el.currentTime = from;
    } catch {
      // As above: nothing to seek yet.
    }
    void el.play()?.then(() => setPlaying(true)).catch(() => undefined);
  }, []);
  const pause = useCallback(() => {
    ref.current?.pause();
    setPlaying(false);
    setNow(null);
  }, []);
  const onTime = () => {
    const el = ref.current;
    if (!el) return;
    setNow(el.currentTime);
    if (stopAt.current !== null && el.currentTime >= stopAt.current) {
      el.pause();
      stopAt.current = null;
      setPlaying(false);
      setNow(null);
    }
  };
  return { ref, seek, play, pause, playing, now, onTime };
}

interface StepChaptersProps {
  webinarId: string;
  /** The rows as the host left them, and the board version they were read on. */
  onNext: (rows: ChapterRow[], version: number) => void;
  /** Rows kept from an earlier visit to this step, in this tab. */
  kept?: ChapterRow[] | null;
}

/** The talk's words and its player, for splitting a chapter where a sentence starts. */
export function useChapterTalk(webinarId: string) {
  const [lines, setLines] = useState<TalkLine[]>([]);
  const [media, setMedia] = useState<Media>({ url: null, token: null, poster: null, duration: 0 });
  useEffect(() => {
    let live = true;
    void api.board
      .lines(webinarId)
      .then((got) => live && setLines(got.lines))
      .catch(() => undefined);
    void api.webinars
      .videoStatus(webinarId)
      .then((v) => live && setMedia({ url: v.hls_url, token: v.media_token ?? null, poster: v.poster_url, duration: v.duration_sec }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [webinarId]);
  return { lines, media };
}

/** The talk's length: the recording's, else the last line heard, else past the last chapter. */
export function talkLength(media: Media, lines: readonly TalkLine[], rows: readonly ChapterRow[]): number {
  const lastLine = lines.length ? (secondsAt(lines[lines.length - 1].ts) ?? 0) + 1 : 0;
  return media.duration || lastLine || (rows.length ? rows[rows.length - 1].t + 60 : 0);
}

/** The chapters as the read found them, with the board version they were read on, for the step that checks them. */
export function useChapterRows(webinarId: string, kept?: ChapterRow[] | null) {
  const [board, setBoard] = useState<BoardStateResponse | null>(null);
  const [rows, setRows] = useState<ChapterRow[]>([]);
  useEffect(() => {
    let live = true;
    void api.board
      .get(webinarId)
      .then((got) => {
        if (!live) return;
        setBoard(got);
        setRows(kept?.length ? kept : chapterRowsOf(got.state));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // `kept` is read once, on the way in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarId]);
  return { board, rows, setRows };
}

export function StepChapters({ webinarId, onNext, kept }: StepChaptersProps) {
  const { board, rows, setRows } = useChapterRows(webinarId, kept);
  const { lines, media } = useChapterTalk(webinarId);

  if (!board) return <div className="card nbw nbload" aria-busy="true" />;
  const duration = talkLength(media, lines, rows);
  return (
    <div className="card nbw">
      <StepHead n={6} of={7} title={<span className="nbchn">{chapterCount(rows, duration)}</span>} />
      <ChaptersBox rows={rows} onRows={setRows} lines={lines} media={media} />
      <div className="nbw-f">
        <button type="button" className="btn p lg" disabled={rows.length === 0} onClick={() => onNext(rows, board.version)}>
          Design the page <Icon name="right" />
        </button>
      </div>
    </div>
  );
}

/** The chapters, seen on the talk: the player, the bar, each row renamed, split where a sentence starts, or joined. */
export function ChaptersBox({ rows, onRows, lines, media }: { rows: ChapterRow[]; onRows: (rows: ChapterRow[]) => void; lines: TalkLine[]; media: Media }) {
  const [sel, setSel] = useState(0);
  const [splitting, setSplitting] = useState<number | null>(null);
  const toast = useToast();
  const duration = talkLength(media, lines, rows);
  const talk = useTalk(media);
  const n = rows.length;
  const at = Math.min(sel, Math.max(0, n - 1));
  /** Where the player stands: the second it plays at, else where the chosen chapter starts. */
  const heardAt = talk.playing && talk.now !== null ? talk.now : n ? rows[at].t : 0;
  const setRows = (next: ChapterRow[] | ((was: ChapterRow[]) => ChapterRow[])) => onRows(typeof next === "function" ? next(rows) : next);

  const change = (next: ChapterRow[], said: string, before: ChapterRow[]) => {
    setRows(next);
    toast({ text: said, icon: "check", action: { label: "Undo", onClick: () => onRows(before) } });
  };

  return (
      <div className="nbchbox">
        <div className="nbch">
          <div className="nbch-top">
            <div className="nbchpl">
              <div className="nbplayer">
                <div className="nbplayer-v">
                  <video
                    ref={talk.ref}
                    playsInline
                    preload="metadata"
                    poster={media.poster ?? undefined}
                    onTimeUpdate={talk.onTime}
                    aria-hidden="true"
                  />
                  {n ? (
                    <span className="nbplayer-c">
                      Chapter {at + 1} · {rows[at].title}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="nbplayer-p"
                    aria-label={talk.playing ? "Pause" : "Hear where this chapter starts"}
                    onClick={() => (talk.playing ? talk.pause() : n && talk.play(rows[at].t, chapterEnd(rows, at, duration)))}
                  >
                    <Icon name={talk.playing ? "pause" : "playf"} fill={!talk.playing} />
                  </button>
                  {n ? (
                    <span className="nbplayer-t">
                      {mmss(heardAt)} / {mmss(duration)}
                    </span>
                  ) : null}
                  {n && duration > 0 ? (
                    <i className="nbplayer-b">
                      <i style={{ width: `${Math.max(0, Math.min(100, (heardAt / duration) * 100))}%` }} />
                    </i>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="nbch-tl">
              {n ? (
                <div className="nbch-now">
                  <span className="pill wine">
                    Chapter {at + 1} of {n}
                  </span>
                  <b>{rows[at].title}</b>
                  <span className="small muted">
                    {mmss(rows[at].t)} to {mmss(chapterEnd(rows, at, duration))} ·{" "}
                    {Math.max(1, Math.round((chapterEnd(rows, at, duration) - rows[at].t) / 60))} min
                  </span>
                </div>
              ) : null}
              <div className="nbch-bar">
                {rows.map((c, i) => (
                  <button
                    type="button"
                    key={`${c.t}-${i}`}
                    className={cx("nbseg", i === at && "on", i % 2 === 1 && "alt")}
                    style={{ left: `${(c.t / Math.max(1, duration)) * 100}%`, width: `${((chapterEnd(rows, i, duration) - c.t) / Math.max(1, duration)) * 100}%` }}
                    title={c.title}
                    onClick={() => {
                      setSel(i);
                      talk.seek(c.t);
                    }}
                  >
                    <span>{i + 1}</span>
                  </button>
                ))}
              </div>
              <div className="nbch-ruler" aria-hidden="true">
                {[0, 600, 1200, 1800, 2400, 3000, 3600]
                  .filter((s) => s < duration * 0.92)
                  .map((s) => (
                    <span key={s} style={{ left: `${(s / Math.max(1, duration)) * 100}%` }}>
                      {mmss(s)}
                    </span>
                  ))}
                <span style={{ right: 0 }}>{mmss(duration)}</span>
              </div>
            </div>
          </div>
          <div className="nbch-list">
            {rows.map((c, i) => (
              <div key={`${c.t}-${i}`}>
                <div className={cx("nbchrow", i === at && "on")}>
                  <button
                    type="button"
                    className="nbcht"
                    title="Listen from here"
                    onClick={() => {
                      setSel(i);
                      talk.play(c.t, chapterEnd(rows, i, duration));
                    }}
                  >
                    <Icon name="playf" fill />
                    {mmss(c.t)}
                  </button>
                  <input dir="auto"
                    className="inp"
                    value={c.title}
                    aria-label={`Chapter ${i + 1} title`}
                    onFocus={() => setSel(i)}
                    onChange={(e) => setRows((r) => r.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))}
                  />
                  <div className="nbcha">
                    <button
                      type="button"
                      className="btn s q"
                      aria-expanded={splitting === i}
                      onClick={() => {
                        setSel(i);
                        setSplitting(splitting === i ? null : i);
                      }}
                    >
                      <Icon name="scissors" /> Split
                    </button>
                    {i < n - 1 ? (
                      <button
                        type="button"
                        className="btn s q"
                        onClick={() => {
                          const before = rows;
                          setSplitting(null);
                          setSel(i);
                          change(joinRows(rows, i), `Joined. “${c.title}” now takes in “${rows[i + 1].title}”.`, before);
                        }}
                      >
                        <Icon name="merge" /> Join with next
                      </button>
                    ) : (
                      <span className="nbcha-pad" />
                    )}
                  </div>
                </div>
                {splitting === i ? (
                  <SplitPanel
                    rows={rows}
                    index={i}
                    duration={duration}
                    lines={lines}
                    media={media}
                    onCancel={() => setSplitting(null)}
                    onSplit={(t, title) => {
                      const before = rows;
                      setSplitting(null);
                      setSel(i + 1);
                      change(splitRows(rows, i, t, title), `Split at ${mmss(t)}: “${title || "New chapter"}”.`, before);
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
  );
}

interface SplitPanelProps {
  rows: ChapterRow[];
  index: number;
  duration: number;
  lines: TalkLine[];
  media: Media;
  onCancel: () => void;
  onSplit: (t: number, title: string) => void;
}

/** The cut, drawn: the frame at it, the words on either side, a track that snaps to sentence starts. */
function SplitPanel({ rows, index, duration, lines, media, onCancel, onSplit }: SplitPanelProps) {
  const start = rows[index].t;
  const end = chapterEnd(rows, index, duration);
  const cuts = useMemo(() => cutsIn(rows, index, duration, lines), [rows, index, duration, lines]);
  const [k, setK] = useState(() => Math.floor((cuts.length - 1) / 2));
  const [name, setName] = useState<string | null>(null);
  const [slid, setSlid] = useState(false);
  const [dragging, setDragging] = useState(false);
  const track = useRef<HTMLDivElement>(null);
  const frame = useTalk(media);
  const panel = useRef<HTMLDivElement>(null);

  const cut = cuts[Math.max(0, Math.min(cuts.length - 1, k))];
  const pct = cut ? ((cut.t - start) / Math.max(1, end - start)) * 100 : 0;

  // The cut slides in from the chapter's start, so the eye follows it to where it lands.
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setSlid(true)));
    panel.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    track.current?.focus({ preventScroll: true });
    return () => cancelAnimationFrame(id);
  }, []);
  useEffect(() => {
    if (cut) frame.seek(cut.t);
  }, [cut, frame]);

  if (!cut) {
    return (
      <div className="nbsp nbsp-none" ref={panel}>
        <p className="small muted">This chapter has no sentence to start a new one on.</p>
        <div className="nbsp-f">
          <span className="sp" />
          <button type="button" className="btn s q" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const nearest = (x: number) => {
    const r = track.current?.getBoundingClientRect();
    if (!r || r.width === 0) return k;
    const t = start + Math.max(0, Math.min(1, (x - r.left) / r.width)) * (end - start);
    let best = 0;
    cuts.forEach((c, j) => {
      if (Math.abs(c.t - t) < Math.abs(cuts[best].t - t)) best = j;
    });
    return best;
  };
  const down = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    setK(nearest(e.clientX));
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (dragging) setK(nearest(e.clientX));
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const to = { ArrowLeft: k - 1, ArrowDown: k - 1, ArrowRight: k + 1, ArrowUp: k + 1, Home: 0, End: cuts.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    setK(Math.max(0, Math.min(cuts.length - 1, to)));
  };
  const suggested = nameFromLine(cut.said);
  const shownName = name ?? suggested;
  const bars = waveBars(start, end, cuts.map((c) => c.t));
  const before = lineBefore(lines, cut.t);
  const place = slid ? pct : 0;

  return (
    <div className={cx("nbsp", slid && "nbsp-anim", dragging && "nbsp-drag")} ref={panel}>
      <div className="nbsp-view">
        <div className="nbsp-frame">
          <video ref={frame.ref} muted playsInline preload="metadata" poster={media.poster ?? undefined} aria-hidden="true" />
          <span className="nbsp-at">{mmss(cut.t)}</span>
        </div>
        <div className="nbsp-words">
          <p className="nbsp-before">{before ? `“…${before}”` : ""}</p>
          <span className="nbsp-snip">
            <Icon name="scissors" />
          </span>
          <p className="nbsp-after">“{cut.said}”</p>
        </div>
      </div>
      <div
        className="nbsp-track"
        ref={track}
        role="slider"
        tabIndex={0}
        aria-label={`Where to split ${rows[index].title}`}
        aria-valuemin={start}
        aria-valuemax={end}
        aria-valuenow={cut.t}
        aria-valuetext={`${mmss(cut.t)}: ${cut.said}`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        onKeyDown={key}
      >
        <div className="nbsp-wave" aria-hidden="true">
          {bars.map((h, i) => (
            <i key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="nbsp-pa" style={{ width: `${place}%` }}>
          <b>{rows[index].title}</b>
          <span>{mmss(cut.t - start)}</span>
        </div>
        <div className="nbsp-pb" style={{ left: `${place}%`, width: `${100 - place}%` }}>
          <b>{shownName || "New chapter"}</b>
          <span>{mmss(end - cut.t)}</span>
        </div>
        {cuts.map((c) => (
          <i key={c.t} className="nbsp-tick" style={{ left: `${((c.t - start) / Math.max(1, end - start)) * 100}%` }} aria-hidden="true" />
        ))}
        <div className="nbsp-h" style={{ left: `${place}%` }} aria-hidden="true">
          <span className="nbsp-hb">
            <Icon name="scissors" />
          </span>
        </div>
      </div>
      <div className="nbsp-scale">
        <span>{mmss(start)}</span>
        <span>{mmss(end)}</span>
      </div>
      <div className="nbsp-f">
        <label className="nbsp-name">
          <span>New chapter</span>
          <input dir="auto" className="inp" value={shownName} onChange={(e) => setName(e.target.value)} />
        </label>
        <span className="sp" />
        <button type="button" className="btn s q" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn s p" onClick={() => onSplit(cut.t, shownName)}>
          <Icon name="scissors" /> Split here
        </button>
      </div>
    </div>
  );
}
